import React, { memo, useCallback, useMemo, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import { Dropdown } from '../../form/fields'
import { Option } from '../../form/fields/dropdown/types'
import { ContentPanel } from '../..'

import * as styles from './inventory.module.scss'
import { InventoryProps } from './types'
import WarehouseCard from './warehouseCard/warehouseCard'
import RemoveWarehouseModal from './removeWarehouseModal/removeWarehouseModal'
import {
   convertInventoryWarehouseToOption,
   convertOptionToInventoryWarehouse,
   sortInventoryWarehouses,
} from './helpers'
import { ActionMeta } from 'react-select'
import InventoryAmounts from './inventoryAmounts/inventoryAmounts'
import { ToolTypes } from '../../../../types'

const Inventory = ({
   itemForm,
   warehouseOptions,
   isNewItem,
   handleWorkflowAction,
   handleAddToKitClick,
   handleAdjustClick,
   selectedWarehouses,
   setSelectedWarehouses,
   isQuantityOrConsumableToolType,
   permissions,
   isReadonly,
   isCopyAction
}: InventoryProps) => {
   const [selectedWarehouseId, setSelectedWarehouseId] = useState<
      number | null
   >(null)
   const [isRemovalModalOpen, setIsRemovalModalOpen] = useState<boolean>(false)

   const isConsumble = useMemo(
      () => itemForm.ToolType === ToolTypes['Consumable'],
      [itemForm?.ToolType]
   )

   const filteredOptions = useMemo(
      () =>
         isQuantityOrConsumableToolType
            ? warehouseOptions?.filter(
                 (opt) =>
                    !selectedWarehouses.some(
                       (warehouse) => warehouse.label === opt.label
                    )
              )
            : warehouseOptions,
      [warehouseOptions, selectedWarehouses, isQuantityOrConsumableToolType]
   )

   // for quantity and consumable items
   const handleWarehousesChange = useCallback(
      (selectedOptions: Option[], actionMeta: ActionMeta<Option>) => {
         // user removes a single warehouse
         if (actionMeta.action === 'remove-value' && !isNewItem && !isCopyAction) {
            const warehouseRemoved = selectedWarehouses?.find(
               (wh) => wh.label === actionMeta.removedValue.label
            )
            if (warehouseRemoved?.quantity !== 0) {
               setSelectedWarehouseId(Number(actionMeta.removedValue.value))
               return setIsRemovalModalOpen(true)
            }
         }
         // user adds a warehouse
         if (selectedOptions?.length > 0) {
            const homeWarehouses = selectedOptions.map((opt) => {
               const formWarehouse = itemForm?.Warehouses?.find(
                  (wh) => wh?.WarehouseLocationName === opt.label
               )
               return convertOptionToInventoryWarehouse(opt, formWarehouse)
            })
            const warehousesToSet = [
               ...selectedWarehouses.filter((wh) => !wh.isHomeWarehouse),
               ...homeWarehouses,
            ]
            setSelectedWarehouses(sortInventoryWarehouses(warehousesToSet))
         } else {
            // need to keep non-home warehouses in array
            const warehousesToSet = [
               ...selectedWarehouses.filter((wh) => !wh.isHomeWarehouse),
            ]
            setSelectedWarehouses(warehousesToSet)
         }
      },
      [selectedWarehouses, itemForm, isNewItem]
   )

   // for standard and kit items
   const handleSingleWarehouseChange = useCallback(
      (selectedOption: Option, actionMeta: ActionMeta<Option>) => {
         if (actionMeta?.removedValue) {
            const formWarehouse = itemForm?.Warehouses?.find(
               (wh) =>
                  wh.WarehouseLocationName === actionMeta.removedValue.label
            )
            if (formWarehouse.AvailableQuantity !== 0) {
               setSelectedWarehouseId(Number(actionMeta.removedValue.value))
               return setIsRemovalModalOpen(true)
            }
         }
         if (selectedOption) {
            const formWarehouse = itemForm?.Warehouses?.find(
               (wh) => wh.WarehouseLocationName === selectedOption.label
            )
            const warehouseToSet = convertOptionToInventoryWarehouse(
               selectedOption,
               formWarehouse
            )
            setSelectedWarehouses([warehouseToSet])
         } else {
            setSelectedWarehouses([])
         }
      },
      [selectedWarehouses, itemForm]
   )

   const handleQuantityChange = useCallback(
      (id: number, quantity: number) => {
         setSelectedWarehouses((prevWarehouses) =>
            sortInventoryWarehouses(
               prevWarehouses.map((warehouse) =>
                  warehouse.id === id ? { ...warehouse, quantity } : warehouse
               )
            )
         )
      },
      [selectedWarehouses]
   )

   const handleReorderLevelChange = useCallback(
      (id: number, reorderLevel: number) => {
         setSelectedWarehouses((prevWarehouses) =>
            sortInventoryWarehouses(
               prevWarehouses.map((warehouse) =>
                  warehouse.id === id
                     ? {
                          ...warehouse,
                          reorderLevel,
                       }
                     : warehouse
               )
            )
         )
      },
      [selectedWarehouses]
   )

   const handleLoan = useCallback(
      (id: number) => {
         const defaultWarehouse =
            selectedWarehouses.find((warehouse) => warehouse.id == id) ?? null
         handleWorkflowAction('Loan', defaultWarehouse, true)
      },
      [handleWorkflowAction, selectedWarehouses]
   )

   const handleTransfer = useCallback(
      (id: number) => {
         setIsRemovalModalOpen(false)
         const defaultWarehouse =
            selectedWarehouses.find((warehouse) => warehouse.id == id) ?? null
         handleWorkflowAction('Warehouse Transfer', defaultWarehouse, true)
      },
      [handleWorkflowAction, selectedWarehouses]
   )

   const handleAddToKit = useCallback(
      (id: number) => {
         setIsRemovalModalOpen(false)
         const defaultWarehouse =
            selectedWarehouses.find((warehouse) => warehouse.id == id) ?? null
         handleAddToKitClick(defaultWarehouse, true)
      },
      [handleAddToKitClick, selectedWarehouses]
   )

   const handleAdjust = useCallback(
      (id: number) => {
         setIsRemovalModalOpen(false)
         const defaultWarehouse =
            selectedWarehouses.find((warehouse) => warehouse.id == id) ?? null
         handleAdjustClick(defaultWarehouse, true)
      },
      [handleAdjustClick, selectedWarehouses]
   )

   const currentHomeWarehouses = useMemo(
      () =>
         selectedWarehouses
            .filter((wh) => wh.isHomeWarehouse)
            .map((wh) => convertInventoryWarehouseToOption(wh)),
      [selectedWarehouses]
   )

   return (
      <Row
         gutterWidth={20}
         style={{
            minHeight: '500px',
         }}
      >
         {isRemovalModalOpen && (
            <RemoveWarehouseModal
               isOpen={isRemovalModalOpen}
               setIsOpen={setIsRemovalModalOpen}
               handleTransfer={handleTransfer}
               handleAdjust={handleAdjust}
               selectedWarehouseId={selectedWarehouseId}
            />
         )}
         <Col xs={12} sm={4}>
            <ContentPanel>
               <h1>
                  Home Warehouse Location
                  {isQuantityOrConsumableToolType ? 's' : ''}
               </h1>
               <p>
                  {isReadonly ? 'The' : 'Choose the'} Home Warehouse Location
                  {isQuantityOrConsumableToolType ? 's' : ''} where this Item
                  will be available.
               </p>
               <Dropdown
                  id="Warehouses"
                  name="Warehouses"
                  defaultValue={currentHomeWarehouses}
                  placeholder={null}
                  onChange={
                     isQuantityOrConsumableToolType
                        ? handleWarehousesChange
                        : handleSingleWarehouseChange
                  }
                  options={filteredOptions}
                  isMulti={isQuantityOrConsumableToolType}
                  withWrappers
                  isClearable={false}
                  disabled={!isCopyAction && isReadonly}
               />
               {isQuantityOrConsumableToolType && (
                  <>
                     <h2>Total Inventory Levels</h2>
                     <InventoryAmounts
                        total={itemForm?.Quantity}
                        inWarehouses={itemForm?.AvailableQuantity}
                        pending={itemForm?.PendingRequestQuantity}
                        loaned={itemForm?.LoanedQuantity}
                        displayLoaned={!isConsumble}
                     />
                  </>
               )}
            </ContentPanel>
         </Col>
         <Col xs={12} sm={8}>
            {isQuantityOrConsumableToolType && (
               <section className={styles.inventoryLevels}>
                  <h2 className={styles.inventoryLevelsHeading}>
                     Current Available Inventory Locations
                  </h2>
                  <p className={styles.infoText}>
                     To update inventory for a Home Warehouse Location, click
                     Adjust. To move items between Locations, click Transfer.
                     Set a Reorder Level for each Location to receive
                     notifications when stock runs low.
                  </p>
                  <ul
                     className={`${styles.cardsList} ${
                        selectedWarehouses?.length > 3
                           ? styles.cardsListScrollable
                           : ''
                     }`}
                  >
                     {selectedWarehouses.map((wh) => (
                        <WarehouseCard
                           key={wh.id}
                           isCopyAction={isCopyAction}
                           warehouse={wh}
                           handleQuantityChange={handleQuantityChange}
                           handleReorderLevelChange={handleReorderLevelChange}
                           isNewItem={isNewItem}
                           handleLoan={handleLoan}
                           handleTransfer={handleTransfer}
                           handleAdjust={handleAdjust}
                           permissions={permissions}
                           isReadonly={isReadonly}
                           handleAddToKit={handleAddToKit}
                        />
                     ))}
                  </ul>
               </section>
            )}
         </Col>
      </Row>
   )
}

export default memo(Inventory)
